import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Users,
  CreditCard,
  TrendingUp,
  DollarSign,
  Crown,
  Building2,
  Zap,
  Search,
  Download,
  Shield,
  Loader2,
  BarChart3,
  Trash2,
  UserPlus,
  Rocket,
  Key,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import EarlyAccessPanel from "@/components/admin/EarlyAccessPanel";
import PluginLicensesPanel from "@/components/admin/PluginLicensesPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planColors = {
  free: "#6b7280",
  pro: "#22c55e",
  enterprise: "#eab308",
  enterprise_annual: "#a855f7",
};

const planIcons = {
  free: Zap,
  pro: Crown,
  enterprise: Building2,
  enterprise_annual: Building2,
};

export default function AdminPage({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newPlan, setNewPlan] = useState("");
  const [updating, setUpdating] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "technician" });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchUsers(), fetchPayments()]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Access denied");
      const data = await response.json();
      setStats(data);
    } catch (error) {
      // Error handled silently
      toast.error("Error al cargar estadísticas");
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (planFilter !== "all") params.append("plan_filter", planFilter);
      if (searchTerm) params.append("search", searchTerm);

      const response = await fetch(
        `${API_URL}/api/admin/users?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Access denied");
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      // Error handled silently
    }
  };

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (paymentFilter !== "all") params.append("status_filter", paymentFilter);

      const response = await fetch(
        `${API_URL}/api/admin/payments?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Access denied");
      const data = await response.json();
      setPayments(data.payments || []);
    } catch (error) {
      // Error handled silently
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchUsers();
    }
  }, [planFilter, searchTerm]);

  useEffect(() => {
    if (!loading) {
      fetchPayments();
    }
  }, [paymentFilter]);

  const handleUpdatePlan = async () => {
    if (!selectedUser || !newPlan) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/users/${selectedUser.id}/subscription?plan_id=${newPlan}`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to update");

      toast.success(`Plan actualizado a ${newPlan.toUpperCase()}`);
      setShowPlanDialog(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error("Error al actualizar el plan");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/users/${selectedUser.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete");
      }

      toast.success(`Usuario ${selectedUser.email} eliminado`);
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.message || "Error al eliminar usuario");
    } finally {
      setUpdating(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Completa todos los campos");
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newUser),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create user");
      }

      toast.success(`Usuario ${newUser.email} creado`);
      setShowAddUserDialog(false);
      setNewUser({ email: "", password: "", name: "", role: "technician" });
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.message || "Error al crear usuario");
    } finally {
      setUpdating(false);
    }
  };

  const exportPaymentsCSV = () => {
    const headers = ["Fecha", "Usuario", "Email", "Plan", "Monto", "Moneda", "Estado"];
    const rows = payments.map((p) => [
      new Date(p.created_at).toLocaleDateString(),
      p.user?.name || "N/A",
      p.user?.email || p.user_email,
      p.plan_id,
      p.amount,
      p.currency.toUpperCase(),
      p.payment_status,
    ]);

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pagos_avx_rf_pro_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  if (user?.role !== "admin") {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="bg-gray-900/50 border-red-900 max-w-md">
            <CardContent className="pt-6 text-center">
              <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Acceso Denegado</h2>
              <p className="text-gray-400">
                Solo los administradores pueden acceder a este panel.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-500" />
        </div>
      </Layout>
    );
  }

  const pieData = stats
    ? [
        { name: "Free", value: stats.subscriptions_by_plan.free, color: planColors.free },
        { name: "Pro", value: stats.subscriptions_by_plan.pro, color: planColors.pro },
        { name: "Enterprise", value: stats.subscriptions_by_plan.enterprise, color: planColors.enterprise },
      ]
    : [];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-green-500" />
              Panel de Administración
            </h1>
            <p className="text-gray-400 mt-1">
              Gestiona usuarios, suscripciones y pagos
            </p>
          </div>
        </div>

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-gray-900 border border-gray-700">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-green-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-green-600">
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-green-600">
              <CreditCard className="w-4 h-4 mr-2" />
              Pagos
            </TabsTrigger>
            <TabsTrigger value="early-access" className="data-[state=active]:bg-[#34C759]">
              <Rocket className="w-4 h-4 mr-2" />
              Early Access
            </TabsTrigger>
            <TabsTrigger value="plugin-licenses" className="data-[state=active]:bg-green-600">
              <Key className="w-4 h-4 mr-2" />
              Licencias Plugin
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Usuarios</p>
                      <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Suscriptores Pro</p>
                      <p className="text-3xl font-bold text-green-400">
                        {stats?.subscriptions_by_plan?.pro || 0}
                      </p>
                    </div>
                    <Crown className="w-10 h-10 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Ingresos USD</p>
                      <p className="text-3xl font-bold text-white">
                        ${stats?.payments?.total_revenue_usd?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <DollarSign className="w-10 h-10 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Ingresos CLP</p>
                      <p className="text-3xl font-bold text-white">
                        ${stats?.payments?.total_revenue_clp?.toLocaleString("es-CL") || "0"}
                      </p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Subscriptions Pie Chart */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Distribución de Planes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: "#1f2937", border: "none" }}
                          labelStyle={{ color: "#fff" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-6 mt-4">
                    {pieData.map((item) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-gray-400 text-sm">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Growth Bar Chart */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Crecimiento de Usuarios</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats?.growth_data || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                        <YAxis stroke="#9ca3af" fontSize={12} />
                        <Tooltip
                          contentStyle={{ backgroundColor: "#1f2937", border: "none" }}
                          labelStyle={{ color: "#fff" }}
                        />
                        <Bar dataKey="users" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Summary */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Resumen de Transacciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-gray-400 text-sm">Total Transacciones</p>
                    <p className="text-2xl font-bold text-white">
                      {stats?.payments?.total_transactions || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Pagadas</p>
                    <p className="text-2xl font-bold text-green-400">
                      {stats?.payments?.paid_transactions || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Pendientes</p>
                    <p className="text-2xl font-bold text-yellow-400">
                      {stats?.payments?.pending_transactions || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 justify-between">
              <div className="flex flex-col md:flex-row gap-4 flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por nombre o email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-gray-900 border-gray-700 text-white"
                    data-testid="user-search-input"
                  />
                </div>
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="w-48 bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Filtrar por plan" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">Todos los planes</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="enterprise_annual">Enterprise Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => setShowAddUserDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="add-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Agregar Usuario
              </Button>
            </div>

            {/* Users Table */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-700">
                      <TableHead className="text-gray-400">Usuario</TableHead>
                      <TableHead className="text-gray-400">Email</TableHead>
                      <TableHead className="text-gray-400">Rol</TableHead>
                      <TableHead className="text-gray-400">Plan</TableHead>
                      <TableHead className="text-gray-400">Sistemas</TableHead>
                      <TableHead className="text-gray-400">Pagos</TableHead>
                      <TableHead className="text-gray-400">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => {
                      const PlanIcon = planIcons[u.subscription?.plan_id] || Zap;
                      return (
                        <TableRow key={u.id} className="border-gray-800">
                          <TableCell className="text-white font-medium">
                            {u.name}
                          </TableCell>
                          <TableCell className="text-gray-400">{u.email}</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                u.role === "admin"
                                  ? "bg-red-900/50 text-red-400"
                                  : "bg-gray-800 text-gray-400"
                              }
                            >
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <PlanIcon
                                className="w-4 h-4"
                                style={{ color: planColors[u.subscription?.plan_id] }}
                              />
                              <span
                                style={{ color: planColors[u.subscription?.plan_id] }}
                                className="font-medium"
                              >
                                {u.subscription?.plan_name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {u.stats?.systems || 0}
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {u.stats?.payments || 0}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-gray-600 text-gray-300 hover:bg-gray-800"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setNewPlan(u.subscription?.plan_id || "free");
                                  setShowPlanDialog(true);
                                }}
                                data-testid={`edit-user-${u.id}`}
                              >
                                Cambiar Plan
                              </Button>
                              {u.id !== user?.id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-800 text-red-400 hover:bg-red-900/50"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowDeleteDialog(true);
                                  }}
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 justify-between">
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-48 bg-gray-900 border-gray-700 text-white">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="paid">Pagados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={exportPaymentsCSV}
                className="bg-green-600 hover:bg-green-700"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </div>

            {/* Payments Table */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-700">
                      <TableHead className="text-gray-400">Fecha</TableHead>
                      <TableHead className="text-gray-400">Usuario</TableHead>
                      <TableHead className="text-gray-400">Plan</TableHead>
                      <TableHead className="text-gray-400">Monto</TableHead>
                      <TableHead className="text-gray-400">Estado</TableHead>
                      <TableHead className="text-gray-400">Session ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.id} className="border-gray-800">
                        <TableCell className="text-gray-400">
                          {new Date(p.created_at).toLocaleDateString("es-CL", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{p.user?.name}</p>
                            <p className="text-gray-500 text-sm">{p.user?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            style={{
                              backgroundColor: `${planColors[p.plan_id]}20`,
                              color: planColors[p.plan_id],
                            }}
                          >
                            {p.plan_id.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white font-mono">
                          {p.currency === "clp"
                            ? `$${p.amount.toLocaleString("es-CL")} CLP`
                            : `$${p.amount.toFixed(2)} USD`}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              p.payment_status === "paid"
                                ? "bg-green-900/50 text-green-400"
                                : p.payment_status === "pending"
                                ? "bg-yellow-900/50 text-yellow-400"
                                : "bg-red-900/50 text-red-400"
                            }
                          >
                            {p.payment_status === "paid"
                              ? "Pagado"
                              : p.payment_status === "pending"
                              ? "Pendiente"
                              : p.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500 font-mono text-xs">
                          {p.session_id?.substring(0, 20)}...
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Early Access Tab */}
          <TabsContent value="early-access" className="space-y-4">
            <EarlyAccessPanel />
          </TabsContent>

          {/* Plugin Licenses Tab */}
          <TabsContent value="plugin-licenses" className="space-y-4">
            <PluginLicensesPanel />
          </TabsContent>
        </Tabs>

        {/* Change Plan Dialog */}
        <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
          <DialogContent className="bg-gray-900 border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-white">Cambiar Plan de Usuario</DialogTitle>
              <DialogDescription className="text-gray-400">
                Cambiar el plan de suscripción para {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-400 mb-2">Email: {selectedUser?.email}</p>
              <p className="text-gray-400 mb-4">
                Plan actual:{" "}
                <span className="text-white font-medium">
                  {selectedUser?.subscription?.plan_name}
                </span>
              </p>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Seleccionar nuevo plan" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro ($9.990 CLP/mes)</SelectItem>
                  <SelectItem value="enterprise">Enterprise ($24.990 CLP/mes)</SelectItem>
                  <SelectItem value="enterprise_annual">Enterprise Anual ($150.000 CLP/año)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowPlanDialog(false)}
                className="border-gray-600 text-gray-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdatePlan}
                disabled={updating}
                className="bg-green-600 hover:bg-green-700"
              >
                {updating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Actualizando...
                  </>
                ) : (
                  "Guardar Cambios"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-gray-900 border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-white">Eliminar Usuario</DialogTitle>
              <DialogDescription className="text-gray-400">
                ¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 bg-red-900/20 rounded-lg p-4 border border-red-800">
              <p className="text-white font-medium">{selectedUser?.name}</p>
              <p className="text-gray-400 text-sm">{selectedUser?.email}</p>
              <p className="text-red-400 text-xs mt-2">
                Se eliminarán todos sus sistemas, eventos y datos asociados.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                className="border-gray-600 text-gray-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteUser}
                disabled={updating}
                className="bg-red-600 hover:bg-red-700"
              >
                {updating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Usuario
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
          <DialogContent className="bg-gray-900 border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-white">Agregar Nuevo Usuario</DialogTitle>
              <DialogDescription className="text-gray-400">
                Crea una nueva cuenta de usuario
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Nombre</Label>
                <Input
                  placeholder="Nombre completo"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Contraseña</Label>
                <Input
                  type="password"
                  placeholder="Contraseña"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Rol</Label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Seleccionar rol" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="operator">Operador</SelectItem>
                    <SelectItem value="technician">Técnico</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Plan de Suscripción</Label>
                <Select value={newUser.subscription_plan || "free"} onValueChange={(value) => setNewUser({ ...newUser, subscription_plan: value })}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Seleccionar plan" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="enterprise_annual">Enterprise Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddUserDialog(false);
                  setNewUser({ email: "", password: "", name: "", role: "technician" });
                }}
                className="border-gray-600 text-gray-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddUser}
                disabled={updating}
                className="bg-green-600 hover:bg-green-700"
              >
                {updating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Crear Usuario
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
